<template>
  <ConnectivityUsageGraph
    :currentDailyUsageData="currentDailyUsageData"
    :graphTitle="graphTitle"
    :usageType="usageType"
    :timePeriod="usageCaption"
    :usageYear="usageYear"
    :saleStartYear="saleStartYear"
    :usagePeriod="usagePeriod"
    :totalUsage="totalUsage"
    :saleStartPeriod="saleStartPeriod"
    :saleStartDate="saleStartDate"
    @previousMonthUsage="previousMonthUsage"
    @nextMonthUsage="nextMonthUsage"
    @hourlyUsage="hourlyUsage"
    @dailyUsage="dailyUsage"
    @yearlyUsage="yearlyUsage"
    @previousYearlyUsage="previousYearlyUsage"
    @nextYearlyUsage="nextYearlyUsage"
  />
</template>

<script>
import ConnectivityUsageGraph from "@/components/Graphs/ConnectivityUsageGraph.vue";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Usage graph for an Axxess LTE service, backed by the CSV-sourced AxxessLteSummary* roll-ups
 * (endpoint v1/axxesslteusage/...). Mirrors the usage tab of FtthService.vue but for a single
 * axxessLteServiceNumber - it is display only, so there is no live provider dependency.
 */
export default {
  name: "AxxessLteUsageGraph",
  components: { ConnectivityUsageGraph },
  props: {
    axxessLteServiceNumber: { type: [Number, String], required: true },
    saleStartDate: { type: String, default: "" },
    graphTitle: { type: String, default: "Usage" },
  },
  data() {
    // ConnectivityUsageGraph evaluates computed props that call usagePeriod.toString() /
    // usageYear.toString() on its very first render, which happens before this component's
    // mounted() hook - so these must be valid here in data(), not set later in mounted().
    const currentPeriod = this.getCurrentPeriod();
    return {
      usageType: "daily",
      usagePeriod: parseInt(currentPeriod),
      usageYear: ("" + currentPeriod).substring(0, 4),
      usageCaption: "" + this.prettifyBillingPeriod(currentPeriod),
      totalUsage: 0,
      currentDailyUsageData: [],
      saleStartPeriod: null,
      saleStartYear: null,
    };
  },
  mounted() {
    if (this.saleStartDate) {
      this.saleStartPeriod = this.dateToPeriod(this.saleStartDate);
      this.saleStartYear = ("" + this.saleStartPeriod).substring(0, 4);
    }
    this.loadDaily(this.usagePeriod);
  },
  methods: {
    baseUrl() {
      return `${this.$config.aimsAPI}axxesslteusage/${this.axxessLteServiceNumber}`;
    },
    loadDaily(period) {
      this.$http.get(`${this.baseUrl()}/usage/${period}/daily`).then(
        (response) => this.translateCurrentDailyUsageData(response.data),
        (error) => {
          console.error(error);
          this.showError("Error fetching daily usage", error);
        }
      );
    },
    hourlyUsage(day) {
      const lastDay = new Date(new Date(this.usageCaption).getFullYear(), new Date(this.usageCaption).getMonth() + 1, 0).getDate();
      if (day === "previous") {
        day = parseInt(this.usageCaption.split(" ")[0]) - 1;
      } else if (day === "next") {
        day = parseInt(this.usageCaption.split(" ")[0]) + 1;
      }
      if (day < 1) {
        day = new Date(new Date(this.usageCaption).getFullYear(), new Date(this.usageCaption).getMonth(), 0).getDate();
        if (("" + this.usagePeriod).substring(4, 6) === "01") {
          this.usagePeriod = this.usagePeriod - 100 + 11;
          this.usageYear--;
        } else {
          this.usagePeriod--;
        }
      } else if (day > lastDay) {
        day = 1;
        if (("" + this.usagePeriod).substring(4, 6) === "01") {
          this.usagePeriod = this.usagePeriod - 100 + 11;
          this.usageYear++;
        } else {
          this.usagePeriod++;
        }
      }
      this.$http.get(`${this.baseUrl()}/usage/${this.usagePeriod}/day/${day}/hourly`).then(
        (response) => this.translateCurrentHourlyUsageData(response.data, day),
        (error) => {
          console.error(error);
          this.showError("Error fetching hourly usage", error);
        }
      );
    },
    translateCurrentHourlyUsageData(usageData, day) {
      this.currentDailyUsageData = [];
      this.totalUsage = 0;
      this.usageCaption = day + " " + this.prettifyBillingPeriod(this.usagePeriod);
      this.usageType = "hourly";
      usageData.forEach((c) => {
        this.totalUsage += c.inBytes + c.outBytes;
        this.currentDailyUsageData.push({ label: c.hour, downloaded: c.outBytes, uploaded: c.inBytes });
      });
    },
    dailyUsage(month, year) {
      this.usagePeriod = parseInt(`${year}${month}`);
      const startPeriod = parseInt(this.saleStartPeriod);
      if (this.saleStartPeriod && this.usagePeriod < startPeriod) {
        this.usagePeriod = startPeriod;
      }
      if (this.usagePeriod > parseInt(this.getCurrentPeriod())) {
        this.usagePeriod = parseInt(this.getCurrentPeriod());
      }
      this.loadDaily(this.usagePeriod);
    },
    translateCurrentDailyUsageData(usageData) {
      this.totalUsage = 0;
      this.usageCaption = "" + this.prettifyBillingPeriod(this.usagePeriod);
      this.usageType = "daily";
      const data = usageData.map((c) => {
        this.totalUsage += c.inBytes + c.outBytes;
        return { label: c.day + " " + MONTHS[c.month - 1], downloaded: c.outBytes, uploaded: c.inBytes };
      });
      this.currentDailyUsageData = { tooltip: "Click for hourly usage", data };
    },
    previousMonthUsage() {
      if (("" + this.usagePeriod).substring(4, 6) === "01") {
        this.usagePeriod = this.usagePeriod - 100 + 11;
        this.usageYear--;
      } else {
        this.usagePeriod--;
      }
      this.loadDaily(this.usagePeriod);
    },
    nextMonthUsage() {
      if (this.usagePeriod < this.getCurrentPeriod()) {
        if (("" + this.usagePeriod).substring(4, 6) === "12") {
          this.usagePeriod = this.usagePeriod + 100 - 11;
          this.usageYear++;
        } else {
          this.usagePeriod++;
        }
        this.loadDaily(this.usagePeriod);
      }
    },
    yearlyUsage() {
      const qryPeriod = "" + this.usageYear + "12";
      this.usageCaption = this.usageYear;
      this.$http.get(`${this.baseUrl()}/usage/${qryPeriod}/monthly`).then(
        (response) => this.translateMonthlyUsageData(response.data),
        (error) => {
          console.error(error);
          this.showError("Error fetching monthly usage", error);
        }
      );
    },
    previousYearlyUsage() {
      this.usageYear--;
      this.yearlyUsage();
    },
    nextYearlyUsage() {
      this.usageYear++;
      this.yearlyUsage();
    },
    translateMonthlyUsageData(usageData) {
      this.totalUsage = 0;
      this.usageType = "monthly";
      const data = usageData.map((c) => {
        this.totalUsage += c.inBytes + c.outBytes;
        return { label: MONTHS[c.month - 1], downloaded: c.outBytes, uploaded: c.inBytes };
      });
      this.currentDailyUsageData = { tooltip: "Click for daily usage", data };
    },
  },
};
</script>
